/**
 * pitch-tracker.ts — TS bridge contract for the native real-time pitch engine.
 *
 * Single source of truth for the React Native ⇄ native PitchTracker boundary.
 * Types are 1:1 with the muzly-specs / docs SOT:
 *   - NativeF0Frame / NativeFrameContinuity .... docs/native/f0frame-contract.md §1–2
 *   - PitchTrackerConfig (§9), PitchTrackerModule (§6.1),
 *     PitchTrackerEvent (§10), PitchTrackerError (§11) ... native-module-architecture.md
 *   - DiagnosticsSnapshot tree .................. native-diagnostics-spec.md §5–6
 *   - RealtimePerformanceMetrics ............... native-realtime-performance-risks.md §7
 *
 * Invariants honoured here (GOAL.md "NEVER VIOLATE"):
 *   - `pitch_frame` carries an Analysis Frame only — never raw PCM.
 *   - Display smoothing is separate from scoring; this contract exposes raw f0 +
 *     continuity metadata, never interpolated display values.
 *   - Detector swap is config-level; this schema is a stable, versioned contract.
 *
 * A few helper types (PitchTrackerSessionInfo, AudioInputConfig, PitchOutputConfig,
 * AudioInterruptionReason, SessionStopReason) are referenced but not shaped by the
 * spec; they are inferred minimally and flagged `inferred:` below.
 */

// ──────────────────────────────────────────────────────────────────────────
// Config (native-module-architecture.md §9)
// ──────────────────────────────────────────────────────────────────────────

export type PitchAlgorithm = 'mpm' | 'yin' | 'crepe' | 'hybrid';

/**
 * 프레임을 **실제로 만든** 추정기 id. `PitchAlgorithm`의 상위집합이다.
 *
 * 브라우저 디버그 콘솔은 네이티브 엔진 없이 실제 마이크로 프레임을 만들 수 있는데, 그건
 * JS ACF(`@muzly/voice-engine`의 `autoCorrelate`)이고 **네이티브 대응물이 없다**. 프레임이
 * 자기를 만들지 않은 알고리즘 이름을 달면 안 되므로 여기만 넓힌다 — config의
 * `analysis.algorithm`은 `PitchAlgorithm` 그대로다(`'acf'`는 선택 목록에 없고, 네이티브
 * `make_detector`가 거부한다).
 *
 * ACF는 MPM의 대용이 아니다: 같은 녹음 15개에서 두 추정기의 클립 중앙 f0가 5개는 100센트
 * 이상, 2개는 한 옥타브 넘게 엇갈렸다(2026-07-29 실측). 화면은 그 차이를 표시해야 한다.
 */
export type FrameAlgorithm = PitchAlgorithm | 'acf';
export type SampleRateStrategy = 'native' | 'downsample';
export type SmoothingMode = 'off' | 'light' | 'medium';

export type PitchTrackerConfig = {
  version: 1;
  audio: AudioInputConfig;
  analysis: PitchAnalysisConfig;
  output: PitchOutputConfig;
};

export type PitchAnalysisConfig = {
  algorithm: PitchAlgorithm;
  sampleRateStrategy: SampleRateStrategy;
  minFrequencyHz: number;
  maxFrequencyHz: number;
  windowSize: number;
  hopSize: number;
  silenceThresholdDb: number;
  confidenceThreshold: number;
  smoothing: SmoothingMode;
  /** Experimental-only; stabilised settings get promoted to explicit fields (§9). */
  algorithmOptions?: Record<string, unknown>;
};

/** inferred: spec references AudioInputConfig (§9) but does not shape it; mirror the
 *  C ABI audio fields (MuzlyPitchConfig: capture_sample_rate, channel_count). */
export type AudioInputConfig = {
  captureSampleRate: number;
  channelCount: number;
  /**
   * 이 세션 동안 **반주가 같이 나가나**. 켜면 네이티브가 오디오 세션을 재생이 들리는
   * 모드로 잡는다 — iOS `.measurement`는 시스템 처리를 끄는 대신 출력 게인을 떨어뜨려
   * 반주가 안 들린다(`docs/native/platform-audio-session-policy.md` §3의 caveat, 실측
   * 2026-09-07 iOS 노래 진단). 반주 없는 세션은 켜지 않는다: 그쪽은 입력이 깨끗한 편이
   * 이득이다.
   *
   * 세션 시작에만 먹는다(정책 §7의 session-reset 분류) — `updateConfig`로는 못 바꾼다.
   */
  backingTrack?: boolean;
};

/** inferred: spec references PitchOutputConfig (§9) but does not shape it. Models the
 *  "native analysis rate vs JS event rate are separable" principle (§10). */
export type PitchOutputConfig = {
  /** Throttle for JS-side event emission; native analysis rate stays independent. */
  eventIntervalMs?: number;
  /** Whether to emit `performance_metrics` debug events. */
  includePerformanceMetrics?: boolean;
  /**
   * Write the captured audio to a WAV file in the app sandbox for this session.
   *
   * **Defaults to off** — `privacy-and-data-handling.md` §3 makes recording opt-in.
   * The realtime stream never crosses the bridge — JS learns the path
   * (`getLastRecording`), not the samples, which is what keeps the GOAL.md "raw PCM never
   * crosses to JS **continuously**" invariant intact. A finished diagnosis take is a
   * separate matter: it is read back out (`readRecording`) and uploaded, and the local file
   * is deleted once it lands (ADR-0007).
   *
   * This flag lives on the output config, **not** on `AudioInputConfig` — that type
   * mirrors the C ABI struct (`MuzlyPitchConfig`), and recording is an
   * Obj-C/Kotlin-layer concern that `pitch-core` never sees.
   */
  recordToFile?: boolean;
};

/**
 * A recording the native layer wrote for a session (`recordToFile`).
 *
 * Written to the app sandbox and user-deletable (`deleteRecording`).
 * `privacy-and-data-handling.md` §3: a **diagnosis** take is uploaded and the local file is
 * deleted once it lands (ADR-0007); everything else stays on the device.
 * This struct still carries no samples — metadata and a path. The bytes travel only when
 * something asks for them by that path (`readRecording`).
 */
export type NativeRecording = {
  /** Absolute file path / URI inside the app sandbox. */
  uri: string;
  /** The rate the file was written at — the capture rate, no resampling. */
  sampleRate: number;
  channelCount: number;
  /**
   * **부동소수로 건넌다**(양 플랫폼 모두). 정수로 보내면 안 된다: 44.1kHz에서 표본 수는
   * 13시간쯤에 Int32를 넘고, `putInt`는 그때 조용히 잘린다.
   */
  sampleCount: number;
  durationSec: number;
  /**
   * How many capture discontinuities (ring overrun / input dropout) happened while
   * this file was being written.
   *
   * **Non-zero means the audio has holes**: dropped samples are simply absent, so the
   * file is shorter than wall-clock and does not line up 1:1 with frame timestamps.
   * Reported rather than hidden — the same honesty rule as `NativeFrameContinuity`.
   *
   * Crosses as a float for the same reason as `sampleCount`.
   */
  gapCount: number;
  // ─── 녹음 조건(선택) — SPEC 요청 문서 `AnalyCapture`와 같은 이름·의미(plan Spec).
  // 네이티브가 **사실만** 싣는다. 모르는 값은 키를 빼고, 추측값을 채우지 않는다.
  /** 녹음이 열린 플랫폼. */
  platform?: 'ios' | 'android';
  /** 실제로 연 캡처 소스 — iOS 엔진 입력 `'engine_input'`, Android `MediaRecorder.AudioSource`. */
  captureSource?: 'engine_input' | 'unprocessed' | 'voice_performance' | 'mic';
  /** 오디오 세션 모드 — 측정 3종(sustain·low·high)은 `'measurement'`, 노래는 `'default'`. */
  sessionMode?: 'measurement' | 'default';
  /** 경로 범주 — `libs/shared/src/utils`의 `toInputRoute`가 원시 포트 타입에서 판정한다. */
  inputRoute?: 'builtin' | 'wired' | 'bluetooth' | 'usb' | 'other';
  /** 판정 전의 원시 포트 타입(iOS `AVAudioSessionPort*` · Android `AudioDeviceInfo.type`). */
  inputRouteRaw?: string | number;
  /** iOS `selectedDataSource.dataSourceName` 같은 입력 데이터 소스 이름. */
  inputDataSource?: string;
  /** 녹음 중 백킹 트랙(노래 음원)이 재생됐나. */
  backingTrack?: boolean;
  /** WAV 표본 깊이 — 16bit PCM 고정. */
  bitDepth?: number;
  /** gap이 난 시점의 파일 기준 표본 오프셋(`gapCount`와 같은 시점에 센다, 최대 64개). */
  gapOffsets?: number[];
  /** F0 경로가 저하 프레임으로 표시한 횟수 — 테이크 단위 누적. */
  degradedFrameCount?: number;
  /** 클램프 전 `|x| >= 1.0` 표본 수. */
  clipSampleCount?: number;
  /** 클램프 전 피크의 dBFS. 무음(피크 0)이면 키를 뺀다 — `-inf`는 JSON에 못 싣는다. */
  peakDbfs?: number;
  osVersion?: string;
  deviceModel?: string;
  appVersion?: string;
};

// ──────────────────────────────────────────────────────────────────────────
// NativeF0Frame (docs/native/f0frame-contract.md §1–2)
// ──────────────────────────────────────────────────────────────────────────

/** Reasons a capture/analysis stream can lose continuity (f0frame-contract §1, §10). */
export type StreamDiscontinuityReason =
  | 'buffer_overrun'
  | 'input_dropout'
  | 'analysis_resync'
  | 'timestamp_gap'
  | 'unknown';

export type NativeFrameContinuity = {
  droppedInputFrames: number;
  droppedAnalysisFrames: number;
  discontinuity: boolean;
  discontinuityReason?: StreamDiscontinuityReason;
};

/**
 * Canonical engine output = stable contract. Rules:
 *   - `f0Hz === null` ⇒ `midi` and `note` are both `null`.
 *   - `timestampMs` is the capture-timeline timestamp of the window's FIRST sample.
 *   - never carries raw PCM.
 *   - `continuity` / `latencyEstimateMs` are realtime-only (P3+); the offline F0
 *     core omits them.
 *   - `smoothedF0Hz` is DISPLAY-only and never a scoring input (see below).
 */
export type NativeF0Frame = {
  timestampMs: number;
  f0Hz: number | null;
  midi: number | null;
  note: string | null;
  /**
   * Display-only stabilized F0 (muzly_pitch.h §v2 `smoothed_f0_hz`): median +
   * voiced hysteresis + octave-jump suppression, computed natively alongside the
   * raw estimate. `null` when `analysis.smoothing` is `'off'` or the smoother has
   * not engaged — never a silent copy of `f0Hz`, so a consumer can tell the two
   * apart. Optional because the offline core and the mock module omit it.
   *
   * Draw it, never score it (GOAL.md: UI smoothing != scoring). Scoring reads
   * `f0Hz`; see `scoring/score-frame.ts`.
   */
  smoothedF0Hz?: number | null;
  confidence: number;
  voiced: boolean;
  rmsDb: number;
  /**
   * Harmonics-to-noise ratio (dB), computed natively from the same ACF the detector
   * already runs (f0frame-contract §1). Meaningful only while `voiced` — consumers
   * gate on that. Optional: the offline core, the mock module and any native binary
   * older than this contract omit it, and a missing value is NOT 0 dB.
   */
  hnrDb?: number;
  /**
   * High-band energy ratio proxy for vocal resonance, normalised to `0..1`
   * (f0frame-contract §1). The band edges are owned by the native implementation;
   * this contract fixes only the range. Same voiced-gating and same
   * absent-is-not-zero rule as `hnrDb`.
   */
  resonanceRatio?: number;
  /**
   * `resonanceRatio` read out in dB — the SAME band measurement, converted
   * natively so JS never takes a log of a contract field (f0frame-contract §1,
   * v4). Relative to the window's own energy, so it does not move with mic gain.
   * Present exactly when `resonanceRatio` is.
   */
  resonanceLevelDb?: number;
  /**
   * Centre of gravity (Hz) of the energy in the ring band (f0frame-contract §1,
   * v4). The band is deliberately WIDER than the one `resonanceRatio` sums, so
   * this value can fall below the range the diagnosis screen calls good — the
   * native implementation owns both edges.
   */
  resonanceCenterHz?: number;
  /** Spectral centroid (Hz) — how bright the window reads. Range owned natively. */
  brightnessHz?: number;
  /** Spread (Hz) of the energy about `brightnessHz` — how wide the ring is. */
  bandwidthHz?: number;
  /** 이 프레임을 만든 추정기. 브라우저 마이크 소스는 `'acf'`(네이티브 대응물 없음). */
  algorithm: FrameAlgorithm;
  captureSampleRate: number;
  analysisSampleRate: number;
  continuity?: NativeFrameContinuity;
  latencyEstimateMs?: number;
};

/**
 * Native-owned scales for the resonance fields above. These are COPIES of
 * pitch-core constants (`voice_quality.hpp`: `kResonanceCenterLowHz` /
 * `kResonanceCenterHighHz`, `kResonanceLevelDbFloor`) and they live here, beside
 * the frame contract they describe, so a native change is reviewed in ONE file
 * instead of in every screen that draws a meter. Screens read these instead of
 * transcribing the numbers again.
 */
export const RESONANCE_CENTER_BAND_HZ = { min: 1500, max: 5000 };

/** Native floor of `resonanceLevelDb` (`kResonanceLevelDbFloor`). */
export const RESONANCE_LEVEL_FLOOR_DB = -40;

// ──────────────────────────────────────────────────────────────────────────
// Diagnostics snapshot (native-diagnostics-spec.md §5–6)
// ──────────────────────────────────────────────────────────────────────────

export type SessionDiagnostics = {
  sessionId: string | null;
  state:
    | 'idle'
    | 'starting'
    | 'running'
    | 'paused'
    | 'stopping'
    | 'stopped'
    | 'error';
  startedAtMs: number | null;
  uptimeMs: number;
  configVersion: number;
  engineVersion: string;
  lastErrorCode?: string;
};

export type AudioInputDiagnostics = {
  backend: 'avaudioengine' | 'audiounit' | 'audiorecord' | 'oboe' | 'unknown';
  permissionState: 'granted' | 'denied' | 'not_determined' | 'unknown';
  actualSampleRate: number | null;
  channelCount: number | null;
  inputBufferSizeFrames: number | null;
  callbackCount: number;
  callbackAvgMs: number | null;
  callbackMaxMs: number | null;
  callbackOverBudgetCount: number;
  inputDropoutCount: number;
  lastCallbackAgeMs: number | null;
};

export type RingBufferDiagnostics = {
  capacityMs: number;
  backlogMs: number;
  fillRatio: number;
  writeCount: number;
  readCount: number;
  overrunCount: number;
  underrunCount: number;
  droppedInputFrames: number;
  droppedAnalysisFrames: number;
  lastDiscontinuityReason?: string;
};

export type AnalysisDiagnostics = {
  workerState:
    | 'idle'
    | 'running'
    | 'paused'
    | 'overloaded'
    | 'stopped'
    | 'error';
  analysisSampleRate: number | null;
  windowSize: number;
  hopSize: number;
  frameCount: number;
  frameSkipCount: number;
  avgProcessMs: number | null;
  p95ProcessMs: number | null;
  maxProcessMs: number | null;
  deadlineMissCount: number;
  lastFrameAgeMs: number | null;
};

export type DetectorDiagnostics = {
  algorithm: 'mpm' | 'yin' | 'crepe' | 'hybrid' | 'unknown';
  active: boolean;
  voicedRatio: number | null;
  avgConfidence: number | null;
  lowConfidenceFrameCount: number;
  octaveCorrectionCount: number;
  f0MinHz: number;
  f0MaxHz: number;
};

export type PostProcessorDiagnostics = {
  smoothingMode: SmoothingMode;
  heldFrameCount: number;
  interpolatedFrameCount: number;
  releasedFrameCount: number;
  discontinuityCount: number;
  lastOutputState: 'measured' | 'held' | 'interpolated' | 'missing' | 'unknown';
};

export type EventDispatcherDiagnostics = {
  eventQueueDepth: number;
  pitchEventIntervalMs: number;
  emittedPitchFrameCount: number;
  droppedPitchFrameCount: number;
  emittedErrorCount: number;
  lastEventAgeMs: number | null;
};

export type UiBridgeDiagnostics = {
  lastSnapshotAgeMs: number;
  diagnosticsPollingCount: number;
  diagnosticsDroppedCount: number;
  lastJsConsumerLagMs: number | null;
  snapshotStale: boolean;
};

export type DegradationDiagnostics = {
  level: 0 | 1 | 2 | 3 | 4 | 5;
  reason:
    | 'none'
    | 'deadline_miss'
    | 'ring_buffer_backlog'
    | 'event_queue_pressure'
    | 'thermal'
    | 'repeated_overload'
    | 'unknown';
  sinceMs: number | null;
  action:
    | 'none'
    | 'event_rate_reduced'
    | 'frame_skip'
    | 'resync'
    | 'experimental_branch_disabled'
    | 'low_performance_mode';
};

export type ThermalState =
  | 'nominal'
  | 'fair'
  | 'serious'
  | 'critical'
  | 'unknown';

export type PlatformDiagnostics = {
  platform: 'ios' | 'android';
  osVersion?: string;
  deviceModel?: string;
  thermalState?: ThermalState;
  audioRoute?: 'speaker' | 'wired' | 'bluetooth' | 'receiver' | 'unknown';
  estimatedInputLatencyMs?: number | null;
};

export type DiagnosticsSnapshot = {
  schemaVersion: 1;
  timestampMs: number;
  session: SessionDiagnostics;
  audioInput: AudioInputDiagnostics;
  ringBuffer: RingBufferDiagnostics;
  analysis: AnalysisDiagnostics;
  detector: DetectorDiagnostics;
  postProcessor: PostProcessorDiagnostics;
  eventDispatcher: EventDispatcherDiagnostics;
  uiBridge: UiBridgeDiagnostics;
  degradation: DegradationDiagnostics;
  platform: PlatformDiagnostics;
};

// ──────────────────────────────────────────────────────────────────────────
// Realtime performance metrics (native-realtime-performance-risks.md §7)
// ──────────────────────────────────────────────────────────────────────────

export type RealtimePerformanceMetrics = {
  timestampMs: number;
  audioCallbackAvgMs: number;
  audioCallbackMaxMs: number;
  ringBufferBacklogMs: number;
  ringBufferOverrunCount: number;
  inputDropoutCount: number;
  timestampDiscontinuityCount: number;
  analysisAvgMs: number;
  analysisP95Ms: number;
  analysisDeadlineMissCount: number;
  eventQueueDepth: number;
  jsEventDroppedCount: number;
  pitchFrameEmittedCount: number;
  pitchFrameDroppedCount: number;
  uiHeldFrameCount: number;
  uiInterpolatedFrameCount: number;
  cpuUsageEstimate?: number;
  thermalState?: ThermalState;
};

// ──────────────────────────────────────────────────────────────────────────
// Errors (native-module-architecture.md §11)
// ──────────────────────────────────────────────────────────────────────────

export type PitchTrackerErrorCode =
  | 'microphone_permission_denied'
  | 'microphone_unavailable'
  | 'audio_session_failed'
  | 'unsupported_sample_rate'
  | 'unsupported_config'
  | 'analysis_engine_failed'
  | 'resampler_failed'
  | 'buffer_overrun'
  | 'buffer_underrun'
  | 'device_interrupted'
  | 'internal_error'
  | 'unknown';

export type PitchTrackerError = {
  code: PitchTrackerErrorCode;
  message: string;
  recoverable: boolean;
  platform?: 'ios' | 'android';
  nativeCode?: string;
};

// ──────────────────────────────────────────────────────────────────────────
// Events (native-module-architecture.md §10)
// ──────────────────────────────────────────────────────────────────────────

export type StreamDiscontinuityInfo = {
  timestampMs: number;
  reason: StreamDiscontinuityReason;
  gapMs?: number;
  droppedInputFrames?: number;
  droppedAnalysisFrames?: number;
  recoverable: boolean;
};

/** inferred: spec references PitchTrackerSessionInfo (start() / session_started) but
 *  does not shape it; minimal session identity + the resolved capture/analysis rates. */
export type PitchTrackerSessionInfo = {
  sessionId: string;
  startedAtMs: number;
  captureSampleRate: number;
  analysisSampleRate: number;
  config: PitchTrackerConfig;
};

/** inferred: spec references AudioInterruptionReason (interruption_started) but does
 *  not enumerate it; common interruption causes. */
export type AudioInterruptionReason =
  | 'phone_call'
  | 'other_audio'
  | 'route_change'
  | 'media_services_reset'
  | 'unknown';

/** inferred: spec references SessionStopReason (session_stopped) but does not
 *  enumerate it. */
export type SessionStopReason =
  | 'user_requested'
  | 'error'
  | 'interrupted'
  | 'unknown';

export type PitchTrackerEvent =
  | { type: 'session_started'; session: PitchTrackerSessionInfo }
  | { type: 'pitch_frame'; frame: NativeF0Frame }
  | { type: 'input_level'; timestampMs: number; rmsDb: number }
  | { type: 'stream_discontinuity'; info: StreamDiscontinuityInfo }
  | { type: 'performance_metrics'; metrics: RealtimePerformanceMetrics }
  | { type: 'config_changed'; config: PitchTrackerConfig }
  | { type: 'interruption_started'; reason: AudioInterruptionReason }
  | { type: 'interruption_ended' }
  | { type: 'error'; error: PitchTrackerError }
  | { type: 'session_stopped'; reason: SessionStopReason };

export type PitchTrackerEventName = PitchTrackerEvent['type'];

/** Narrow a PitchTrackerEvent to the payload of a single event name. */
export type PitchTrackerEventOf<N extends PitchTrackerEventName> = Extract<
  PitchTrackerEvent,
  { type: N }
>;

/** RN EventEmitter-style subscription handle. */
export type Subscription = {
  remove(): void;
};

// ──────────────────────────────────────────────────────────────────────────
// JavaScript API (native-module-architecture.md §6.1)
// ──────────────────────────────────────────────────────────────────────────

export interface PitchTrackerModule {
  start(config: PitchTrackerConfig): Promise<PitchTrackerSessionInfo>;
  stop(): Promise<void>;
  pause(): Promise<void>;
  resume(): Promise<void>;
  updateConfig(config: Partial<PitchTrackerConfig>): Promise<void>;
  getDiagnosticsSnapshot(): Promise<DiagnosticsSnapshot>;
  /**
   * The recording written for the most recent session, or `null` when the session
   * did not opt in (`recordToFile`), the platform has no implementation, or nothing
   * was captured. Call after `stop()` — the file is finalised there.
   */
  getLastRecording?(): Promise<NativeRecording | null>;
  /**
   * Read one finished recording back as base64 (upload path), or `null` when the
   * file is missing or exceeds `maxBytes`. The size is checked before the read.
   */
  readRecording?(
    uri: string,
    maxBytes: number,
  ): Promise<{ base64: string; byteLength: number } | null>;
  /**
   * Delete one recording. Required by `privacy-and-data-handling.md` §3
   * ("사용자가 개별/전체 삭제 가능"); a path that no longer exists is not an error.
   */
  deleteRecording?(uri: string): Promise<void>;
  addListener<N extends PitchTrackerEventName>(
    eventName: N,
    listener: (event: PitchTrackerEventOf<N>) => void,
  ): Subscription;
}
